var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useEffect, useRef, } from 'react';
import { Search, ArrowUp, ArrowDown } from '@one-thd/sui-icons';
import { Typography, Button, Badge, TileGroup, Tile, TextField, InputAdornment, IconButton, Skeleton, SkeletonContent, SkeletonBlock, } from '@one-thd/sui-atomic-components';
import { areAddressesEqual, extractAddressFromFulfillmentGroup, isAddressEmpty, } from '../../../utils/order-details/OrderDetailsUtils';
import { useEditDeliveryAddressContext, useFulfillmentGroupContext, } from '../../../hooks/useContexts';
import { EditDeliveryAddressErrorMessages, } from '../../../models/edit-delivery-address';
import { AlertBanner } from '../../shared/alert-banner/AlertBanner';
import { zipRegex } from '../util/general';
import { PickUpAtDifferentStoreButton, ZipCodeNotEligibleMessage, } from '../util/OutOfStock';
import { getIsZipCodeEligibilityEnabled } from '../../../utils/edit-delivery-address/editDeliveryAddressUtils';
import { isBopisOutOfStockRecoverable } from '../util/FulfillmentStatus';
/**
 * Returns a list of delivery addresses to display, prioritizing the default and current addresses.
 * @param {IDeliveryAddress[]} deliveryAddresses - Array of all delivery addresses.
 * @param {IDeliveryAddress | null} currentAddress - The current delivery address, or null if not set.
 * @returns {IDeliveryAddress[]} Array of addresses to show, ordered by priority.
 */
var getAddressesToShow = function (deliveryAddresses, currentAddress) {
    var defaultAddress = deliveryAddresses.find(function (addr) { return addr.isDefault; });
    // If there is no current address, show all addresses with the default address at the beginning.
    if (!currentAddress) {
        if (defaultAddress) {
            // Default at the beginning, then the rest (no duplicates)
            return __spreadArray([
                defaultAddress
            ], deliveryAddresses.filter(function (addr) { return addr.id !== defaultAddress.id; }), true);
        }
        // If there is no default, return the list as is
        return deliveryAddresses;
    }
    // If there is no default, show current + the rest
    if (!defaultAddress) {
        return __spreadArray([currentAddress], deliveryAddresses, true);
    }
    var filteredAddresses = deliveryAddresses.filter(function (addr) { return addr.id !== defaultAddress.id; });
    // If current and default are equal, show only current + the rest (no duplicates)
    if (areAddressesEqual(currentAddress, defaultAddress)) {
        return __spreadArray([__assign(__assign({}, currentAddress), { isDefault: true })], filteredAddresses, true);
    }
    // If current and default are different, show current, default, and the rest (no duplicates)
    return __spreadArray([currentAddress, defaultAddress], filteredAddresses, true);
};
export var EditDeliveryAddressChooseAddress = function (_a) {
    var setCountry = _a.setCountry, handleClearAll = _a.handleClearAll, setIsAddressList = _a.setIsAddressList, setIsDefault = _a.setIsDefault, isExcludedAddress = _a.isExcludedAddress, isSameAddress = _a.isSameAddress, setIsSameAddress = _a.setIsSameAddress, setLocalCities = _a.setLocalCities, setIsValidated = _a.setIsValidated, setInvalidField = _a.setInvalidField, setIsZipCodeChanged = _a.setIsZipCodeChanged, handleZipCodeValueChange = _a.handleZipCodeValueChange, handleZipCodeStatus = _a.handleZipCodeStatus, invalidField = _a.invalidField, isSkeletonLoading = _a.isSkeletonLoading;
    var _b = useEditDeliveryAddressContext(), zipCodeValue = _b.formState.zipCodeValue, updateFormField = _b.updateFormField, deliveryAddresses = _b.deliveryAddresses, isEligible = _b.isEligible, isEligibilityChecked = _b.isEligibilityChecked, setIsEligibilityChecked = _b.setIsEligibilityChecked, selectedAddressId = _b.selectedAddressId, setSelectedAddressId = _b.setSelectedAddressId;
    var fulfillmentGroup = useFulfillmentGroupContext().fulfillmentGroup;
    var errorRef = useRef(null);
    var _c = React.useState(false), showAllAddresses = _c[0], setShowAllAddresses = _c[1];
    var _d = React.useState(''), validZipCode = _d[0], setValidZipCode = _d[1];
    var rawCurrentAddress = extractAddressFromFulfillmentGroup(fulfillmentGroup.shippingAndHandling ||
        fulfillmentGroup.storePickup ||
        fulfillmentGroup.bodfsDeliveryAndHandling, fulfillmentGroup.fulfillmentType);
    var isZipCodeEligibilityEnabled = getIsZipCodeEligibilityEnabled(fulfillmentGroup);
    var currentAddress = useMemo(function () {
        if (isAddressEmpty(rawCurrentAddress)) {
            return null;
        }
        return __assign(__assign({ id: 'current' }, rawCurrentAddress), { isDefault: false, isCurrent: true });
    }, []);
    function getFilteredAddressesToShow() {
        if (!isZipCodeEligibilityEnabled) {
            if (deliveryAddresses.length > 0) {
                return getAddressesToShow(deliveryAddresses, currentAddress);
            }
            return [];
        }
        if (!isEligibilityChecked) {
            return null;
        }
        if (deliveryAddresses.length > 0 && isEligible && validZipCode) {
            var allAddresses = getAddressesToShow(deliveryAddresses, currentAddress);
            // Should return addresses that match the zip code
            return allAddresses.filter(function (addr) { return addr.zipCode === validZipCode; });
        }
        return [];
    }
    var addressesToShow = getFilteredAddressesToShow();
    useEffect(function () {
        if (addressesToShow && addressesToShow.length > 0) {
            var isSelectedAddressInList = selectedAddressId &&
                addressesToShow.some(function (addr) { return addr.id === selectedAddressId; });
            // Select first when nothing selected OR current selection no longer exists
            if (!selectedAddressId || !isSelectedAddressInList) {
                var address = addressesToShow[0];
                setSelectedAddressId(address.id);
                updateFormField({
                    firstNameValue: address.firstName || '',
                    lastNameValue: address.lastName || '',
                    streetAddressValue: address.addressLine1 || '',
                    addressLineTwoValue: address.addressLine2 || null,
                    zipCodeValue: address.zipCode || '',
                    cityStateValue: "".concat(address.city || '', ", ").concat(address.state || ''),
                    phoneNumberValue: rawCurrentAddress.phoneNumber,
                });
                setCountry(address.country || '');
                setIsDefault(address.isDefault || false);
            }
        }
    }, [addressesToShow]);
    useEffect(function () {
        if ((zipCodeValue === null || zipCodeValue === void 0 ? void 0 : zipCodeValue.length) === 5) {
            setValidZipCode(zipCodeValue);
        }
    }, [zipCodeValue]);
    useEffect(function () {
        var _a, _b;
        if ((isExcludedAddress || isSameAddress) && errorRef.current) {
            errorRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            (_b = (_a = errorRef.current).focus) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
    }, [isExcludedAddress, isSameAddress]);
    var handleAddNewAddress = function () {
        handleClearAll();
        if (isZipCodeEligibilityEnabled && zipCodeValue) {
            updateFormField({
                zipCodeValue: zipCodeValue,
            });
        }
        setIsAddressList(false);
        updateFormField({
            phoneNumberValue: rawCurrentAddress.phoneNumber,
        });
    };
    var formatText = function (text) {
        return text
            .split(' ')
            .map(function (word) { return word === null || word === void 0 ? void 0 : word.toLowerCase(); }) // map each word, ensuring all letters are lowercase
            .map(function (word) { return (word ? word[0].toUpperCase() + word.slice(1) : ''); }) // map each word, capitalizing the first letter
            .join(' ');
    };
    var handleAddressChange = function (_event, id) {
        setSelectedAddressId(id);
        var selectedAddr = addressesToShow === null || addressesToShow === void 0 ? void 0 : addressesToShow.find(function (addr) { return addr.id === id; });
        if (selectedAddr) {
            setIsSameAddress(false);
            updateFormField({
                firstNameValue: selectedAddr.firstName || '',
                lastNameValue: selectedAddr.lastName || '',
                streetAddressValue: selectedAddr.addressLine1 || '',
                addressLineTwoValue: selectedAddr.addressLine2 || null,
                zipCodeValue: selectedAddr.zipCode || '',
                cityStateValue: "".concat(selectedAddr.city || '', ", ").concat(selectedAddr.state || ''),
                phoneNumberValue: rawCurrentAddress.phoneNumber,
            });
            setCountry(selectedAddr.country || '');
            setIsDefault(selectedAddr.isDefault || false);
        }
    };
    var renderBadges = function (item) {
        var badgeLabel = null;
        if (item.isCurrent) {
            badgeLabel = 'Current';
        }
        else if (item.isDefault) {
            badgeLabel = 'Default';
        }
        var variant = selectedAddressId === item.id ? 'strong' : 'regular';
        return badgeLabel ? (React.createElement("div", { className: "sui-flex sui-flex-col sui-items-start sui-flex-shrink-0" },
            React.createElement(Badge, { variant: variant, size: "small", color: "medium" }, badgeLabel))) : null;
    };
    // Logic to show only the first 4 addresses or all
    var visibleAddresses = !showAllAddresses && addressesToShow && addressesToShow.length > 4
        ? addressesToShow.slice(0, 4)
        : addressesToShow;
    var isBopisOutOfStock = isBopisOutOfStockRecoverable(fulfillmentGroup);
    var shouldHideAddNewAddressButton = isZipCodeEligibilityEnabled &&
        (zipCodeValue.length !== 5 || handleZipCodeStatus() === 'error');
    return (React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-4", "data-bopis-oos": isZipCodeEligibilityEnabled ? 'true' : 'false' },
        isExcludedAddress && (React.createElement("div", { ref: errorRef, tabIndex: -1 },
            React.createElement(AlertBanner, { status: "error" }, EditDeliveryAddressErrorMessages.EXCLUDED_ADDRESS_SAVED_ADDRESSES))),
        isSameAddress && (React.createElement("div", { ref: errorRef, tabIndex: -1 },
            React.createElement(AlertBanner, { status: "error" },
                React.createElement(Typography, null, EditDeliveryAddressErrorMessages.SAME_ADDRESS_SAVED_ADDRESSES)))),
        React.createElement(Typography, null,
            isZipCodeEligibilityEnabled
                ? "Enter your ZIP Code to see your eligible delivery addresses.".concat(' ')
                : '',
            "You\u2019ll continue to receive order updates at your phone number:",
            ' ',
            React.createElement("span", { className: "sui-font-bold", "data-testid": "delivery-date" }, rawCurrentAddress.phoneNumber)),
        isZipCodeEligibilityEnabled && (React.createElement("div", null,
            React.createElement(TextField, { id: "zipCode", label: "ZIP Code *", placeholder: "Zip Code", value: zipCodeValue, disabled: isSkeletonLoading, InputProps: {
                    endAdornment: (React.createElement(InputAdornment, { position: "end" },
                        React.createElement(IconButton, { disabled: true, "aria-label": "submit-search-by", icon: Search }))),
                    inputProps: { maxLength: '5' },
                }, onChange: function (event) {
                    updateFormField({
                        cityStateValue: '',
                    });
                    setLocalCities([]);
                    setIsEligibilityChecked(false);
                    if (zipRegex.test(event.target.value) ||
                        event.target.value === '') {
                        updateFormField({
                            zipCodeValue: event.target.value,
                        });
                        setIsValidated(true);
                        setIsSameAddress(false);
                        setInvalidField(false);
                    }
                    if (event.target.value.length === 5) {
                        setIsZipCodeChanged(true);
                        handleZipCodeValueChange(event.target.value);
                    }
                }, onBlur: function () {
                    if (zipCodeValue === '' || zipCodeValue.length !== 5) {
                        setInvalidField(true);
                        setIsValidated(false);
                    }
                    else {
                        setInvalidField(false);
                    }
                }, fullWidth: true, status: handleZipCodeStatus(), statusMessage: invalidField && zipCodeValue.length !== 5
                    ? 'Please enter your 5-digit ZIP code.'
                    : null }))),
        isEligibilityChecked && !isEligible && (React.createElement(AlertBanner, { status: "error" },
            React.createElement(Typography, null, ZipCodeNotEligibleMessage(false, fulfillmentGroup)))),
        isSkeletonLoading ? (React.createElement(React.Fragment, null, Array.from({ length: 4 }).map(function (item, index) { return (React.createElement(Skeleton, { key: "skeleton-".concat(index), "data-testid": "skeleton-".concat(index), disablePadding: true, grow: true },
            React.createElement(SkeletonContent, { disablePadding: true },
                React.createElement(SkeletonBlock, { aspect: "wide" })))); }))) : (React.createElement(TileGroup, { value: selectedAddressId, onChange: handleAddressChange, "aria-label": "rate", className: "sui-flex sui-flex-col sui-gap-5" },
            visibleAddresses &&
                visibleAddresses.length > 0 &&
                visibleAddresses.map(function (item) { return (React.createElement(Tile, { key: item.id, value: item.id, "aria-label": "delivery-address-tile", grow: true },
                    React.createElement("div", { className: "sui-flex sui-flex-row sui-justify-between sui-gap-4 sui-w-full sui-p-3" },
                        React.createElement("div", { className: "sui-min-w-0 sui-max-w-full sui-break-words" },
                            React.createElement("div", { className: "sui-mb-3" },
                                React.createElement(Typography, { align: "left", weight: "bold", variant: "h5", height: "tight" },
                                    formatText(item.firstName),
                                    " ",
                                    formatText(item.lastName))),
                            React.createElement(Typography, { align: "left" },
                                formatText(item.addressLine1),
                                React.createElement("br", null),
                                formatText(item.city),
                                ", ",
                                item.state,
                                " ",
                                item.zipCode,
                                React.createElement("br", null))),
                        renderBadges(item)))); }),
            isEligibilityChecked &&
                isEligible &&
                (addressesToShow === null || addressesToShow === void 0 ? void 0 : addressesToShow.length) === 0 && (React.createElement(Typography, null, "You don't have any saved addresses in this ZIP Code. You can add a new address below.")))),
        addressesToShow && addressesToShow.length > 4 && (React.createElement("div", { className: "sui-w-full sui-justify-center sui-flex" },
            React.createElement(Button, { variant: "ghost", disabled: isSkeletonLoading, "aria-label": showAllAddresses ? 'View Less' : 'View More', endIcon: showAllAddresses ? ArrowUp : ArrowDown, onClick: function () { return setShowAllAddresses(function (prev) { return !prev; }); } }, showAllAddresses ? 'View Less' : 'View More'))),
        isEligibilityChecked && !isEligible && isBopisOutOfStock ? (React.createElement(PickUpAtDifferentStoreButton, null)) : (!shouldHideAddNewAddressButton && (React.createElement(Button, { variant: "tertiary", disabled: isSkeletonLoading, fullWidth: true, onClick: handleAddNewAddress }, "Add New Address")))));
};
